import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Button,
  Col,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
  Modal,
  Tab
} from "@themesberg/react-bootstrap";
import { motion } from "framer-motion/dist/framer-motion";
import { useSearchParams } from "react-router-dom";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { promoCodeHeaders } from "./constants";
import AddEditModal from "./components/AddEditModal";
import usePromoCodes from "./hooks/usePromoCodes";
import useVoucherCodes from "./hooks/useVoucherCodes";
import NotificationIconContainer from "../../../components/NotificationIcon/NotificationIconContainer";
import Preloader from "../../../components/Preloader";
import PaginationComponent from "../../../components/Pagination";
import Toggle from "./components/Toggle";
import useCheckPermission from "../../../utils/checkPermission";
import { AddButtonStyled, ButtonContainer, HeaderSection, ResponsiveTitle } from "../TransactionsBanking/style";
import ActionMenu from "../TransactionsBanking/components/ActionMenu";
import { toast } from "../../../components/Toast";
import { isDateValid } from "./constants"
import { DeleteConfirmationModal } from "../../../components/ConfirmationModal"
import useTableCustom from '../../../components/CusotmFilter'
import TableErrorMessage from '../../../components/TableErrorMessage'

import PromoCodesTable from "./components/PromoCodesTable";
import { StyledTabs } from "./style";

export default function PromoCodes() {
  // URL search params for tab state
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl === "voucher" ? "voucher" : "promo");

  // Get current tab's data
  const isVoucherTab = activeTab === "voucher";

  const {
    page: promoPage,
    limit: promoLimit,
    setPage: setPromoPage,
    setLimit: setPromoLimit,
    loading: promoLoading,
    handleCreateEdit: promoHandleCreateEdit,
    handleDelete,
    handleToggleStatus: promoHandleToggleStatus,
    type: promoType,
    data: promoData,
    setShow: promoSetShow,
    show: promoShow,
    createUpdate: promoCreateUpdate,
    dispatch,
    codes: promoCodes,
    validation: promoValidation,
    setValidation: promoSetValidation,
    totalPages: promoTotalPages,
    setSelectedpromocode,
    selectedpromoCode,
    onHandleDeleteConfirmation,
    deletemodal, setDeleteModal,
    totalCount
  } = usePromoCodes();

  // Voucher codes hook - only fetches when isVoucherTab is true
  const {
    page: voucherPage,
    limit: voucherLimit,
    setPage: setVoucherPage,
    setLimit: setVoucherLimit,
    loading: voucherLoading,
    codes: voucherCodes,
    totalPages: voucherTotalPages,
    // Create/Edit related
    type: voucherType,
    data: voucherData,
    show: voucherShow,
    setShow: voucherSetShow,
    validation: voucherValidation,
    setValidation: voucherSetValidation,
    handleCreateEdit: voucherHandleCreateEdit,
    createUpdate: voucherCreateUpdate,
    handleToggleStatus: voucherHandleToggleStatus,
    handleToggleRedeemed: voucherHandleToggleRedeemed,
    // Delete related
    handleDelete: voucherHandleDelete,
    onHandleDeleteConfirmation: voucherOnHandleDeleteConfirmation,
    selectedpromoCode: voucherSelectedpromoCode,
    deletemodal: voucherDeletemodal,
    setDeleteModal: voucherSetDeleteModal,
    currentConversionRate: voucherCurrentConversionRate,
    totalCount: voucherTotalCount
  } = useVoucherCodes(isVoucherTab);

  // Get current tab's create/edit handlers
  const handleCreateEdit = isVoucherTab ? voucherHandleCreateEdit : promoHandleCreateEdit;
  const type = isVoucherTab ? voucherType : promoType;
  const data = isVoucherTab ? voucherData : promoData;
  const show = isVoucherTab ? voucherShow : promoShow;
  const setShow = isVoucherTab ? voucherSetShow : promoSetShow;
  const createUpdate = isVoucherTab ? voucherCreateUpdate : promoCreateUpdate;
  const validation = isVoucherTab ? voucherValidation : promoValidation;
  const setValidation = isVoucherTab ? voucherSetValidation : promoSetValidation;
  const currentConversionRate = isVoucherTab ? voucherCurrentConversionRate : null;

  // Handle tab change with URL update
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "voucher") {
      setSearchParams({ tab: "voucher" });
    } else {
      setSearchParams({});
    }
  };
  const codes = isVoucherTab ? voucherCodes : promoCodes;
  const loading = isVoucherTab ? voucherLoading : promoLoading;
  const page = isVoucherTab ? voucherPage : promoPage;
  const limit = isVoucherTab ? voucherLimit : promoLimit;
  const setPage = isVoucherTab ? setVoucherPage : setPromoPage;
  const setLimit = isVoucherTab ? setVoucherLimit : setPromoLimit;
  const totalPages = isVoucherTab ? voucherTotalPages : promoTotalPages;


  const { isHidden } = useCheckPermission();

  const isShowActionColumn = !isHidden({ module: { key: 'PromoCode', value: 'U' } }) || !isHidden({ module: { key: 'PromoCode', value: 'D' } })
  const isShowStatusColumn = !isHidden({ module: { key: 'PromoCode', value: 'U' } })


  // const dynamicTableHeaders = useMemo(() => {
  //   const headers = [...promoCodeHeaders.filter(col => col.value !== 'action')];

  //   if (isShowStatusColumn) {
  //     headers.push({
  //       label: 'Status',
  //       value: 'status',
  //       inputType: 'toggle',
  //       accessorKey: 'status',
  //       Header: 'Status'
  //     },)

  //   }

  //   if (isShowActionColumn) {
  //     headers.push({
  //       accessorKey: 'action',
  //       Header: 'Actions',
  //       value: 'action',
  //       label: 'Actions',
  //     });

  //   }



  //   return headers;
  // }, [isShowActionColumn]);

  const promoTableHeaders = useMemo(() => {
    return promoCodeHeaders.filter(col => {
      // Promo tab should not show Redeemed or voucher-specific columns
      if (col.value === 'isRedeemed') return false; // Promo tab should not show Redeemed column
      if (col.value === 'amountTtd') return false; // Amount (TTD) is voucher-only
      if (!isShowStatusColumn && col.value === 'status') return false;
      if (!isShowActionColumn && col.value === 'action') return false;
      return true;
    });
  }, [isShowStatusColumn, isShowActionColumn]);

  const voucherTableHeaders = useMemo(() => {
    return promoCodeHeaders
      .filter(col => {
        if (col.value === 'amount') return false; // Voucher table shows only Amount (TTD), not USD
        if (!isShowStatusColumn && col.value === 'status') return false;
        if (!isShowActionColumn && col.value === 'action') return false;
        return true;
      });
  }, [isShowStatusColumn, isShowActionColumn]);

  const {
    visibleColumns: promoVisibleColumns,
    pageData: promoPageData,
    customColSetting: promoCustomColSetting,
    noDataFound: promoNoDataFound,
  } = useTableCustom(
    promoCodes,
    promoTableHeaders, // already permission-filtered
    false,
    () => { },
    null,
    null,
    false,
    "",
    true,
    "PROMO_CODES_TABLE"
  );

  const {
    visibleColumns: voucherVisibleColumns,
    pageData: voucherPageData,
    customColSetting: voucherCustomColSetting,
    noDataFound: voucherNoDataFound,
  } = useTableCustom(
    voucherCodes,
    voucherTableHeaders, // already permission-filtered
    false,
    () => { },
    null,
    null,
    false,
    "",
    true,
    "VOUCHER_CODES_TABLE"
  );

  // Get current tab's table settings
  const visibleColumns = isVoucherTab ? voucherVisibleColumns : promoVisibleColumns;
  const customColSetting = isVoucherTab ? voucherCustomColSetting : promoCustomColSetting;


  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const paginationRef = useRef(null);
  const tabsRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState("");


  useEffect(() => {
    const updateMaxHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const filterHeight = filterRef.current?.offsetHeight || 0;
      const paginationHeight = paginationRef?.current?.offsetHeight || 0;
      const tabTef = tabsRef?.current?.offsetHeight || 0

      const responsiveTable = document.querySelector('.table-responsive');
      const totalHeight = headerHeight + filterHeight + paginationHeight + tabTef;
      const availableHeight = window.innerHeight - totalHeight - 140;

      if (window.innerWidth < 1000) {
        // For smaller screens: reset to full height
        if (responsiveTable) {

          responsiveTable.style.maxHeight = '100%';
          responsiveTable.style.overflowY = 'unset';
        }
        setTableMaxHeight('100%');
        return;
      }
      else if (responsiveTable) {

        responsiveTable.style.maxHeight = `${availableHeight}px`;
        responsiveTable.style.overflowY = 'auto'; // ensure scrolling
        setTableMaxHeight(`${availableHeight}px`);
      }


    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [codes]);

  console.log(tableMaxHeight, "tableMaxHeight")


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  let headers = promoCodeHeaders;

  // Filter headers based on permissions
  headers = isHidden({
    module: { key: "PromoCode", value: "U" },
  })
    ? headers.filter((header) => header.label !== "Status")
    : headers;

  if (
    isHidden({ module: { key: "PromoCode", value: "U" } }) &&
    isHidden({ module: { key: "PromoCode", value: "D" } })
  ) {
    headers = headers.filter(
      (header) => header.label !== "Action"
    );
  }
  return (
    <>
      <HeaderSection className="section-header-wrap" ref={headerRef}>

        <Row className="justify-content-between align-items-center g-3" ref={tabsRef}>
          <Col xs={12} md={6}>
            <ResponsiveTitle className="title-text">
              Codes
            </ResponsiveTitle>
          </Col>

          <Col xs={12} md={6}>
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-end gap-2">
              <div style={{ maxWidth: '200px', flexShrink: 0 }}>
                {customColSetting}
              </div>
              <ButtonContainer className="d-flex justify-content-md-end">
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex-grow-1 flex-md-grow-0"
                  hidden={isHidden({
                    module: { key: "PromoCode", value: "C" },
                  })}
                >
                  <AddButtonStyled
                    variant="primary"
                    onClick={() => handleCreateEdit("Create")}
                  >
                    <AddCircleOutlineRoundedIcon />
                    <span>{activeTab === "promo" ? "Add Promo Code" : "Add Voucher Code"}</span>
                  </AddButtonStyled>
                </motion.div>
                <div className="ms-auto ms-md-2">
                  <NotificationIconContainer />
                </div>
              </ButtonContainer>
            </div>


          </Col>
        </Row>
      </HeaderSection>

      <div className="mt-4">
        <StyledTabs
          id="promo-voucher-tabs"
          activeKey={activeTab}
          onSelect={handleTabChange}
          className="mb-3"
        >
          <Tab eventKey="promo" title="Promo Code">
            {promoLoading ? (
              <Preloader />
            ) : (
              <PromoCodesTable
                codes={promoCodes}
                loading={promoLoading}
                visibleColumns={promoVisibleColumns}
                page={promoPage}
                totalPages={promoTotalPages}
                countText="Total Promo Codes : "
                totalCount={totalCount}
                showTotalCount={true}
                setPage={setPromoPage}
                limit={promoLimit}
                setLimit={setPromoLimit}
                handleCreateEdit={promoHandleCreateEdit}
                handleDelete={handleDelete}
                handleToggleStatus={promoHandleToggleStatus}
                type={promoType}
                onHandleDeleteConfirmation={onHandleDeleteConfirmation}
                selectedpromoCode={selectedpromoCode}
                deletemodal={deletemodal}
                setDeleteModal={setDeleteModal}
                isVoucherTab={false}
              />
            )}
          </Tab>
          <Tab eventKey="voucher" title="Voucher Code">
            {voucherLoading ? (
              <Preloader />
            ) : (
              <PromoCodesTable
                codes={voucherCodes}
                loading={voucherLoading}
                visibleColumns={voucherVisibleColumns}
                page={voucherPage}
                totalPages={voucherTotalPages}
                setPage={setVoucherPage}
                limit={voucherLimit}
                setLimit={setVoucherLimit}
                handleCreateEdit={voucherHandleCreateEdit}
                handleDelete={voucherHandleDelete}
                handleToggleStatus={voucherHandleToggleStatus}
                handleToggleRedeemed={voucherHandleToggleRedeemed}
                type={voucherType}
                onHandleDeleteConfirmation={voucherOnHandleDeleteConfirmation}
                selectedpromoCode={voucherSelectedpromoCode}
                deletemodal={voucherDeletemodal}
                setDeleteModal={voucherSetDeleteModal}
                isVoucherTab={true}

                countText="Total Voucher Codes : "
                totalCount={voucherTotalCount}
                showTotalCount={true}
              />
            )}
          </Tab>
        </StyledTabs>
      </div>

      <AddEditModal
        type={type}
        data={data}
        show={show}
        setShow={setShow}
        createUpdate={createUpdate}
        loading={loading}
        dispatch={dispatch}
        codes={codes}
        validation={validation}
        setValidation={setValidation}
        entityName={activeTab === "promo" ? "Promo Code" : "Voucher Code"}
        isVoucherTab={isVoucherTab}
        currentConversionRate={currentConversionRate}
      />

    </>
  );
}
